import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import { TrackerActivationType } from '@racemap/utilities/consts/trackers';
import { getIdString } from '@racemap/utilities/functions/trackers';
import { isDefined } from '@racemap/utilities/functions/validation';
import { type FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useCurrentUser, useIsAdmin } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import type { ManagedTracker } from '../../../store/trackers/trackers_reducers';
import { Checkbox } from '../../BasicComponents/Checkbox';
import { HStack } from '../../BasicComponents/MetaComponent';
import { TrackerUpdateCount } from '../../BasicComponents/TrackerUpdateCount';
import { FormInputDateTime, FormSelect } from '../../FormComponents';

interface Props {
  trackerIds: Array<ObjectId>;
  show: boolean;
  onClose: () => void;
}

export const TrackerActivationModal: FC<Props> = ({ trackerIds, show = false, onClose }) => {
  const currentUser = useCurrentUser();
  const trackers = useStore((s) => s.trackers.items);
  const trackersToUse = trackerIds
    .map((t) => trackers.get(getIdString(t)))
    .filter(isDefined) as Array<ManagedTracker>;
  const firstTracker = trackersToUse?.[0];
  const [newActivationType, setNewActivationType] = useState(
    firstTracker?.activation.type === TrackerActivationType.ACTIVATED
      ? TrackerActivationType.ARCHIVED
      : TrackerActivationType.ACTIVATED,
  );
  const [paidUntil, setPaidUntil] = useState<Date>();
  const [overwritePaidUntil, setOverwritePaidUntil] = useState(false);
  const isAdmin = useIsAdmin();
  const { updateTrackers } = useStore((s) => s.trackers.actions);

  const handleSubmitClick = async () => {
    if (currentUser == null || trackersToUse == null) return;

    if (isAdmin && newActivationType === TrackerActivationType.ACTIVATED && overwritePaidUntil) {
      updateTrackers(trackerIds, { activation: { type: newActivationType, paidUntil } });
    } else {
      updateTrackers(trackerIds, { activation: { type: newActivationType } });
    }
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change the activation type of the trackers!</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FormSelect<TrackerActivationType>
          value={newActivationType}
          onChange={setNewActivationType}
        >
          <option value={TrackerActivationType.ARCHIVED}>Archive</option>
          <option value={TrackerActivationType.ACTIVATED}>Activate</option>
          {isAdmin && <option value={TrackerActivationType.RACEMAPTRACKER}>Racemap Tracker</option>}
        </FormSelect>
        {isAdmin && newActivationType === TrackerActivationType.ACTIVATED && (
          <>
            <HStack justify="flex-start" gap="5px">
              <Checkbox value={overwritePaidUntil} onChange={setOverwritePaidUntil} />
              <div className="label">Overwrite paid until</div>
            </HStack>
            {overwritePaidUntil && (
              <Container>
                <FormInputDateTime
                  label="Paid until"
                  startOfUnit="second"
                  disabled={!overwritePaidUntil}
                  value={paidUntil?.toISOString() ?? ''}
                  onChange={(newDate: string) => setPaidUntil(new Date(newDate))}
                />
              </Container>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack style={{ width: '100%' }}>
          <TrackerUpdateCount
            targetTrackers={trackersToUse || []}
            total={trackersToUse?.length || 0}
          />
          <Button onClick={handleSubmitClick} disabled={newActivationType == null}>
            Submit
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

const Container = styled.div`
  padding-top: 10px;
`;
